import { BeraPawForgeWithdrawalAbi } from '../abis/berapaw'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, type LstWithdrawalReader } from './shared'

/**
 * BeraPaw LBGT — redemption position on the BeraPawForge.
 *
 * LBGT → BERA exits are tracked per-user by `redeemPosition(account)`
 * returning `(queued, available)` LBGT amounts (1:1 with BERA). There is
 * no per-request id, so this emits at most two normalised entries: the
 * `available` portion as `claimable` and the `queued` portion as
 * `pending`. No deterministic `readyAt` (depends on `queueDelay()` from
 * the request block), so it's left unset.
 */
export const readerBeraPaw: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    const forge = entry.withdrawalContract
    if (!forge) return []

    let res: unknown[]
    try {
      res = (await multicallRetry({
        chain: chainId,
        calls: [
          { address: forge, name: 'redeemPosition', params: [user] },
        ] as unknown as never[],
        abi: [BeraPawForgeWithdrawalAbi] as unknown as never,
      })) as unknown[]
    } catch {
      return []
    }

    const pos = res[0] as
      | { queued?: unknown; available?: unknown }
      | unknown[]
      | undefined
    if (!pos) return []
    const queued = Array.isArray(pos) ? toBigInt(pos[0]) : toBigInt(pos.queued)
    const available = Array.isArray(pos)
      ? toBigInt(pos[1])
      : toBigInt(pos.available)

    const out: LstWithdrawalRequest[] = []
    if (available !== undefined && available > 0n) {
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: 'available',
        amountUnderlying: available.toString(),
        status: 'claimable',
      })
    }
    if (queued !== undefined && queued > 0n) {
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: 'queued',
        amountUnderlying: queued.toString(),
        status: 'pending',
      })
    }
    return out
  },
}
